import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CourseLessonEntity, CourseLessonProgressEntity, QuizEntity, QuizResultEntity, QuizResultStatusAllItems } from '../../../../meta-data/app-meta-data.service';
import { QuizProgressEvent } from '../../models/quiz-progress-event.model';
import { QuizRepositoryService } from '../../services/quiz-repository.service';
import { combineLatest, Observable, of } from 'rxjs';
import { LessonProgressHierarchy } from '../../../courses/models/lesson-progress-hierarchy.model';
import { QuizCompleteResult } from '../../models/quiz-complete-result.model';
import { QuizViewAction, QuizViewActionType } from '../../models/quiz-view-action.enum';
import { QuizHomeView } from '../../models/quiz-home-view.enum';
import { DwSecurityStateService, sortArrayByField } from '@devwareapps/devware-cap';
import { QuizValidationResult } from '../../models/quiz-validation-result.model';
import { CourseRepositoryService } from '../../../courses/services/course-repository.service';
import { CourseNavigationService } from '../../../courses/services/course-navigation.service';
import { tap } from 'rxjs/operators';
import { LessonProgressItem } from '../../../courses/models/course-progress-hierarchy.model';

@Component({
  selector: 'app-quiz-home',
  templateUrl: './quiz-home.component.html',
  styleUrls: ['./quiz-home.component.scss']
})
export class QuizHomeComponent implements OnInit, OnChanges {
  @Input() lesson: CourseLessonEntity;
  @Input() progress: LessonProgressHierarchy;
  @Output() quizEvent = new EventEmitter<QuizProgressEvent>();

  @Input() lessonNavigationInfo: any;
  @Input() prevLessonItem: LessonProgressItem | null = null;
  @Input() nextLessonItem: LessonProgressItem | null = null;

  quiz: QuizEntity;
  quizResults: QuizResultEntity[] = [];
  displayedQuizResults: QuizResultEntity[] = [];

  currentQuizResult: QuizResultEntity;
  allowTakingQuiz = false;

  @Input() view: QuizHomeView = QuizHomeView.notStarted;
  @Output() viewChanged = new EventEmitter<QuizHomeView>();
  quizValidationResult: QuizValidationResult;
  progressJustReset: boolean;
  quizRetakeMessage: boolean;
  loading = true;

  get showQuizStatusAlert(): boolean {
    return !this.loading
      && this.quizValidationResult?.IsValid === false
      && this.view !== QuizHomeView.quiz
      && this.view !== QuizHomeView.quizAnswers;
  }

  constructor(private quizRepositoryService: QuizRepositoryService,
    private courseRepositoryService: CourseRepositoryService,
    private courseNavigationService: CourseNavigationService,
    private securityStateService: DwSecurityStateService) { }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes.lesson?.firstChange == false) {
      if (this.quiz?.QuizId != this.lesson?.QuizId) {
        // Sidebar navigation away from a running attempt: drop the user
        // back to a non-running view BEFORE initQuiz starts fetching the
        // new quiz's data. This unmounts the inner <app-quiz> via *ngIf,
        // which triggers its ngOnDestroy → clears the timer + state from
        // the previous quiz so it can't tick on behind the new lesson.
        // initQuiz will promote the view to lastResult later if the new
        // quiz has prior attempts; otherwise it stays at notStarted.
        this.setView(QuizHomeView.notStarted);
        this.initQuiz();
      }
    }
  }

  ngOnInit(): void {
    this.initQuiz();
  }

  startQuiz() {
    this.currentQuizResult = null;

    //this.view = QuizHomeView.quiz;
    this.setView(QuizHomeView.quiz);
  }


  quizComplete(event: QuizCompleteResult) {
    // Nothing to do if the quiz is cancelled
    if (event.isCancelled) {

      this.quizResultAction({ isCancelled: true })
      return;
    }

    this.currentQuizResult = event.quizResult;
    this.quizResults = [...this.quizResults, this.currentQuizResult];
    this.updateDisplayedQuizResults();

    if (this.currentQuizResult.ProgressReset) {
      this.allowTakingQuiz = false;
    }

    let bestResult = sortArrayByField(this.quizResults, 'PercentageScore', 'QuizStopDateTime', true)[0];


    
    let isComplete =(this.progress.lesson.isComplete || event.quizResult.QuizResultStatusId == QuizResultStatusAllItems.Passed);
    
    //let isComplete = (bestResult.QuizResultStatusId == QuizResultStatusAllItems.Passed);

    this.quizEvent.emit({
      isComplete: isComplete,
      bestQuizResultId: bestResult.QuizResultId,
      quizResult: this.currentQuizResult
    });

    this.validateQuiz().subscribe(() => {
      this.progressJustReset = this.currentQuizResult.ProgressReset;

      this.setView(QuizHomeView.lastResult);
    });
  }

  quizResultAction(action: QuizViewAction) {

    if (action.isCancelled) {
      if (this.currentQuizResult) {
        this.setView(QuizHomeView.lastResult);
        //this.view = QuizHomeView.lastResult;
      } else {
        if (this.quizResults.length > 0) {
          this.currentQuizResult = this.quizResults[this.quizResults.length - 1]
          this.setView(QuizHomeView.lastResult);
          //this.view = QuizHomeView.lastResult;
        } else {
          this.setView(QuizHomeView.notStarted);
          //this.view = QuizHomeView.notStarted;
        }
      }
    }

    switch (action.actionType) {
      case QuizViewActionType.retake:
        this.startQuiz();
        break;
      case QuizViewActionType.viewAnswers:
        //this.view = QuizHomeView.quizAnswers;
        this.setView(QuizHomeView.quizAnswers);
        this.currentQuizResult = action.quizResult;

        break;
      case QuizViewActionType.viewAllResults:
        this.setView(QuizHomeView.allResults);
        //this.view = QuizHomeView.allResults;

        break;
    }

  }

  initQuiz() {
    this.resetQuizState();

    if (!this.lesson?.QuizId) {
      this.loading = false;
      return;
    }

    if (!this.progress?.course?.studentCourse) {
      this.loading = false;
      return;
    }

    this.loading = true;

    const validation$ = this.validateQuiz() ?? of(null);
    const quiz$ = this.quizRepositoryService.getQuiz(this.lesson.QuizId) ?? of(null);
    const quizResults$ = this.quizRepositoryService.getQuizResults(
      this.lesson.QuizId,
      this.progress.course.studentCourse.StudentCourseId,
      this.progress?.lesson?.lessonProgress?.CourseLessonProgressId
    ) ?? of([]);

    combineLatest([validation$, quiz$, quizResults$]).subscribe(([validationResult, quiz, quizResults]) => {
      this.quiz = quiz;
      this.quizResults = quizResults || [];
      this.updateDisplayedQuizResults();

      if (this.quizResults.length > 0) {
        this.currentQuizResult = this.quizResults[this.quizResults.length - 1]

        const quizPassed = this.quizResults.find(r => r.QuizResultStatusId == QuizResultStatusAllItems.Passed) != null;

        if (!this.progress?.lesson?.isComplete && quizPassed) {
          this.quizRetakeMessage = true;
         }


        this.setView(QuizHomeView.lastResult);
        this.loading = false;
        return;
      }

      this.loading = false;
    });
  }

  private resetQuizState() {
    this.quiz = null;
    this.quizResults = [];
    this.displayedQuizResults = [];
    this.currentQuizResult = null;
    this.quizValidationResult = null;
    this.allowTakingQuiz = false;
    this.progressJustReset = false;
    this.quizRetakeMessage = false;
  }

  private updateDisplayedQuizResults(): void {
    this.displayedQuizResults = this.quizResults.filter(result =>
      result.QuizResultStatusId === QuizResultStatusAllItems.Passed
      || result.QuizResultStatusId === QuizResultStatusAllItems.Failed);
  }

  private validateQuiz(): Observable<QuizValidationResult> {
    return this.quizRepositoryService.validateQuiz({
      courseId: this.progress.course.course.CourseId,
      quizId: this.lesson.QuizId,
      courseLessonProgressId: this.progress?.lesson?.lessonProgress?.CourseLessonProgressId,
      courseLessonId: this.lesson.CourseLessonId,
      courseModuleId: this.progress?.module?.module?.CourseModuleId
    }).pipe(tap(validationResult => {
      this.quizValidationResult = validationResult;

      this.allowTakingQuiz = (this.quizValidationResult?.IsValid === true);

    }));
  }

  private setView(view: QuizHomeView) {
    this.view = view;

    this.securityStateService.setLastUserInteraction();

    this.viewChanged.emit(view);
  }


  navigateToPrev() {
    if (!this.lessonNavigationInfo?.previousLesson) return;
    this.courseNavigationService.navigateToLesson(this.lessonNavigationInfo.previousLesson);
  }

  handleNextCardClick() {
    if (!this.lessonNavigationInfo?.nextLesson) return;
    if (this.lessonNavigationInfo?.isLastLesson) {
      this.courseNavigationService.navigateToModule(
        this.lessonNavigationInfo.course?.CourseId,
        this.lessonNavigationInfo.nextLesson?.moduleId
      );
      return;
    }
    this.courseNavigationService.navigateToLesson(this.lessonNavigationInfo.nextLesson);
  }

  isNextCardDisabled(): boolean {
    return !this.lessonNavigationInfo?.nextLesson
      || !this.progress?.lesson?.lessonProgress?.LessonComplete;
  }
}
